import { roomApi } from '../services/api';
import { ROUTES } from '../constants/routes';

/**
 * Determines the appropriate player URL for a room based on its slug
 * @param {string} roomId - The room ID
 * @param {object} userInfo - User information object with isSignedIn and user properties
 * @returns {Promise<string>} The player URL path
 */
export async function getPlayerUrl(roomId, userInfo = null) {
  try {
    // Fetch room data to get slug
    const roomData = await roomApi.getRoom(roomId, userInfo);

    // Always use slug URL if available
    if (roomData.slug) {
      console.log(`✓ Navigating to room via globally unique slug: /${roomData.slug}`);
      return ROUTES.ROOM_BY_SLUG(roomData.slug);
    }

    // Fallback to room ID URL if slug is not available (shouldn't happen with new system)
    console.warn('Room missing slug, falling back to room ID URL');
    return ROUTES.PLAYER(roomId);
  } catch (error) {
    // On error, fall back to room ID URL
    console.warn('Failed to fetch room data for navigation, falling back to room ID URL:', error.message);
    return ROUTES.PLAYER(roomId);
  }
}

/**
 * Generates a shareable URL for a room.
 *
 * Supports three cases:
 *  1. Published snapshot  → /play/:publishedId
 *  2. Room with slug      → /:slug
 *  3. Fallback            → /player/:roomId
 *
 * @param {object} room      - Room object (needs at least { id })
 * @param {string} baseUrl   - Origin, e.g. window.location.origin
 * @param {object} [opts]    - Extra options
 * @param {boolean} opts.isPublished  - Whether this is a published snapshot
 * @param {string}  opts.publishedId  - The published snapshot ID
 * @returns {{ url: string, isSlugUrl: boolean, canShare: boolean }}
 */
export function getShareUrl(room, baseUrl = window.location.origin, opts = {}) {
  const { isPublished = false, publishedId } = opts;

  // Published snapshot gets its own /play/ URL
  if (isPublished && publishedId) {
    return {
      url: `${baseUrl}${ROUTES.PUBLISHED(publishedId)}`,
      isSlugUrl: false,
      canShare: true,
    };
  }

  const roomIsPublic = room.isPublic || false;

  // Prefer globally-unique slug URL
  if (room.slug) {
    return {
      url: `${baseUrl}${ROUTES.ROOM_BY_SLUG(room.slug)}`,
      isSlugUrl: true,
      canShare: roomIsPublic,
    };
  }

  // Fallback to room ID URL
  return {
    url: `${baseUrl}${ROUTES.PLAYER(room.id)}`,
    isSlugUrl: false,
    canShare: false,
  };
}
